import React, { PropsWithChildren, createContext, useEffect, useState, useMemo, useContext } from 'react';
import { ThemeProvider, createTheme } from '@mui/material/styles';
import createCache from '@emotion/cache';
import { CacheProvider } from '@emotion/react';
import rtlPlugin from 'stylis-plugin-rtl';
import { prefixer } from 'stylis';
import { palette, typography, theme as customTheme } from '../theme/colors';
import { LanguageContext } from './LanguageContext';

// Create Emotion caches for LTR and RTL
const ltrCache = createCache({
  key: 'mui',
});

const rtlCache = createCache({
  key: 'muirtl',
  stylisPlugins: [prefixer, rtlPlugin],
});

type ColorModeContextType = {
  mode: 'light' | 'dark';
  setMode: () => void;
};

export const ColorModeContext = createContext<ColorModeContextType>({} as ColorModeContextType);

// Get colors based on mode
const getColors = (mode: 'light' | 'dark') => {
  if (mode === 'light') {
    return {
      background: palette.light.background,
      backgroundElevated: palette.light.backgroundElevated,
      surface: palette.light.surface,
      surfaceElevated: palette.light.surfaceElevated,
      surfaceHover: palette.light.surfaceHover,
      textPrimary: palette.light.textPrimary,
      textSecondary: palette.light.textSecondary,
      textMuted: palette.light.textMuted,
      border: palette.light.border,
      borderLight: palette.light.borderLight,
      sidebarBg: palette.light.sidebarBg,
      sidebarHover: palette.light.sidebarHover,
      sidebarActive: palette.light.sidebarActive,
      sidebarBorder: palette.light.sidebarBorder,
      sidebarText: palette.light.sidebarText,
      sidebarTextActive: palette.light.sidebarTextActive,
    };
  }
  return {
    background: palette.background,
    backgroundElevated: palette.backgroundElevated,
    surface: palette.surface,
    surfaceElevated: palette.surfaceElevated,
    surfaceHover: palette.surfaceHover,
    textPrimary: palette.textPrimary,
    textSecondary: palette.textSecondary,
    textMuted: palette.textMuted,
    border: palette.border,
    borderLight: palette.borderLight,
    sidebarBg: palette.sidebarBg,
    sidebarHover: palette.sidebarHover,
    sidebarActive: palette.sidebarActive,
    sidebarBorder: palette.sidebarBorder,
    sidebarText: palette.sidebarText,
    sidebarTextActive: palette.sidebarTextActive,
  };
};

export const ColorModeContextProvider: React.FC<PropsWithChildren> = ({ children }) => {
  const [mode, setMode] = useState<'light' | 'dark'>('dark');

  // Get direction from LanguageContext for RTL support
  const languageContext = useContext(LanguageContext);
  const direction = languageContext?.direction || 'ltr';

  useEffect(() => {
    const savedMode = localStorage.getItem('colorMode');
    if (savedMode === 'dark' || savedMode === 'light') {
      setMode(savedMode);
    }
  }, []);

  const setColorMode = () => {
    const newMode = mode === 'light' ? 'dark' : 'light';
    setMode(newMode);
    localStorage.setItem('colorMode', newMode);
  };

  const colors = getColors(mode);

  // Select appropriate cache based on direction
  const cache = direction === 'rtl' ? rtlCache : ltrCache;

  const theme = useMemo(
    () =>
      createTheme({
        direction,
        palette: {
          mode,
          primary: {
            main: palette.primary,
            light: palette.primaryLight,
            dark: palette.primaryDark,
            contrastText: palette.textOnPrimary,
          },
          secondary: {
            main: palette.accent,
            light: palette.accentLight,
            dark: palette.accentDark,
          },
          success: {
            main: palette.success,
            light: palette.successLight,
            dark: palette.successDark,
          },
          warning: {
            main: palette.warning,
            light: palette.warningLight,
            dark: palette.warningDark,
          },
          error: {
            main: palette.error,
            light: palette.errorLight,
            dark: palette.errorDark,
          },
          info: {
            main: palette.info,
            light: palette.infoLight,
            dark: palette.infoDark,
          },
          background: {
            default: colors.background,
            paper: colors.surface,
          },
          text: {
            primary: colors.textPrimary,
            secondary: colors.textSecondary,
            disabled: colors.textMuted,
          },
          divider: colors.border,
        },
        typography: {
          fontFamily: typography.fontFamily.body,
          fontSize: 12, // Base font size scaled down from 14
          htmlFontSize: 16,
          h1: {
            fontFamily: typography.fontFamily.display,
            fontWeight: typography.fontWeight.bold,
            fontSize: '1.875rem', // 30px (was ~40px)
            letterSpacing: typography.letterSpacing.tight,
          },
          h2: {
            fontFamily: typography.fontFamily.display,
            fontWeight: typography.fontWeight.bold,
            fontSize: '1.5rem', // 24px (was ~32px)
            letterSpacing: typography.letterSpacing.tight,
          },
          h3: {
            fontFamily: typography.fontFamily.display,
            fontWeight: typography.fontWeight.semibold,
            fontSize: '1.25rem', // 20px (was ~28px)
            letterSpacing: typography.letterSpacing.tight,
          },
          h4: {
            fontFamily: typography.fontFamily.display,
            fontWeight: typography.fontWeight.semibold,
            fontSize: '1rem', // 16px (was ~24px)
          },
          h5: {
            fontFamily: typography.fontFamily.display,
            fontWeight: typography.fontWeight.semibold,
            fontSize: '0.875rem', // 14px (was ~20px)
          },
          h6: {
            fontFamily: typography.fontFamily.display,
            fontWeight: typography.fontWeight.semibold,
            fontSize: '0.8125rem', // 13px (was ~18px)
          },
          subtitle1: {
            fontWeight: typography.fontWeight.medium,
            fontSize: '0.8125rem', // 13px
          },
          subtitle2: {
            fontWeight: typography.fontWeight.medium,
            fontSize: '0.75rem', // 12px
            letterSpacing: typography.letterSpacing.wide,
          },
          body1: {
            fontSize: '0.8125rem', // 13px (was 16px)
            lineHeight: typography.lineHeight.relaxed,
          },
          body2: {
            fontSize: '0.75rem', // 12px (was 14px)
            lineHeight: typography.lineHeight.normal,
          },
          button: {
            fontSize: '0.75rem', // 12px
            fontWeight: typography.fontWeight.semibold,
            letterSpacing: typography.letterSpacing.wide,
            textTransform: 'none' as const,
          },
          caption: {
            fontSize: '0.6875rem', // 11px
          },
          overline: {
            fontFamily: typography.fontFamily.mono,
            fontSize: '0.625rem', // 10px
            fontWeight: typography.fontWeight.medium,
            letterSpacing: typography.letterSpacing.widest,
            textTransform: 'uppercase' as const,
          },
        },
        shape: {
          borderRadius: 10,
        },
        components: {
          MuiCssBaseline: {
            styleOverrides: {
              body: {
                scrollbarWidth: 'thin',
                scrollbarColor: `${colors.border} transparent`,
                '&::-webkit-scrollbar': {
                  width: '8px',
                  height: '8px',
                },
                '&::-webkit-scrollbar-track': {
                  background: 'transparent',
                },
                '&::-webkit-scrollbar-thumb': {
                  background: colors.border,
                  borderRadius: '4px',
                },
                '&::-webkit-scrollbar-thumb:hover': {
                  background: colors.borderLight,
                },
              },
            },
          },
          MuiAppBar: {
            styleOverrides: {
              root: {
                boxShadow: 'none',
                backgroundImage: 'none',
              },
            },
          },
          MuiButton: {
            styleOverrides: {
              root: {
                borderRadius: customTheme.radius.md,
                padding: '7px 14px',
                fontSize: typography.fontSize.base,
                transition: customTheme.transitions.normal,
              },
              contained: {
                '&:hover': {
                  transform: 'translateY(-1px)',
                },
              },
              outlined: {
                borderWidth: '1.5px',
                '&:hover': {
                  borderWidth: '1.5px',
                  backgroundColor: colors.surfaceHover,
                },
              },
            },
          },
          MuiCard: {
            styleOverrides: {
              root: {
                backgroundImage: 'none',
                borderRadius: customTheme.radius.xl,
                border: `1px solid ${colors.border}`,
              },
            },
          },
          MuiPaper: {
            styleOverrides: {
              root: {
                backgroundImage: 'none',
              },
            },
          },
          MuiTextField: {
            styleOverrides: {
              root: {
                '& .MuiOutlinedInput-root': {
                  borderRadius: customTheme.radius.md,
                  '&:hover .MuiOutlinedInput-notchedOutline': {
                    borderColor: palette.primary,
                  },
                  '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
                    borderColor: palette.primary,
                    borderWidth: '2px',
                  },
                },
              },
            },
          },
          MuiChip: {
            styleOverrides: {
              root: {
                fontWeight: typography.fontWeight.medium,
                borderRadius: customTheme.radius.md,
                height: 24,
                fontSize: '0.6875rem',
              },
              sizeSmall: {
                height: 20,
                fontSize: '0.625rem',
              },
            },
          },
          MuiInputBase: {
            styleOverrides: {
              root: {
                fontSize: '0.8125rem',
              },
              sizeSmall: {
                fontSize: '0.75rem',
              },
            },
          },
          MuiOutlinedInput: {
            styleOverrides: {
              root: {
                fontSize: '0.8125rem',
              },
              input: {
                padding: '10px 12px',
              },
              inputSizeSmall: {
                padding: '6px 10px',
              },
            },
          },
          MuiInputLabel: {
            styleOverrides: {
              root: {
                fontSize: '0.8125rem',
              },
              sizeSmall: {
                fontSize: '0.75rem',
              },
            },
          },
          MuiTableCell: {
            styleOverrides: {
              root: {
                fontSize: '0.75rem',
                padding: '10px 12px',
              },
              head: {
                fontSize: '0.6875rem',
                fontWeight: typography.fontWeight.semibold,
              },
              sizeSmall: {
                padding: '6px 10px',
              },
            },
          },
          MuiMenuItem: {
            styleOverrides: {
              root: {
                fontSize: '0.8125rem',
                minHeight: 36,
              },
            },
          },
          MuiSelect: {
            styleOverrides: {
              select: {
                fontSize: '0.8125rem',
              },
            },
          },
          MuiTab: {
            styleOverrides: {
              root: {
                fontSize: '0.75rem',
                minHeight: 40,
                padding: '8px 14px',
              },
            },
          },
          MuiTabs: {
            styleOverrides: {
              root: {
                minHeight: 40,
              },
            },
          },
          MuiDrawer: {
            styleOverrides: {
              paper: {
                borderRight: 'none',
              },
            },
          },
          MuiListItemButton: {
            styleOverrides: {
              root: {
                borderRadius: customTheme.radius.md,
                margin: '2px 8px',
                transition: customTheme.transitions.fast,
                '&.Mui-selected': {
                  backgroundColor: colors.sidebarActive,
                  '&:hover': {
                    backgroundColor: colors.sidebarActive,
                  },
                },
                '&:hover': {
                  backgroundColor: colors.sidebarHover,
                },
              },
            },
          },
          MuiDialog: {
            styleOverrides: {
              paper: {
                borderRadius: customTheme.radius.xl,
              },
            },
          },
          MuiAlert: {
            styleOverrides: {
              root: {
                borderRadius: customTheme.radius.lg,
              },
            },
          },
          MuiAvatar: {
            styleOverrides: {
              root: {
                fontFamily: typography.fontFamily.display,
                fontWeight: typography.fontWeight.semibold,
              },
            },
          },
          MuiTooltip: {
            styleOverrides: {
              tooltip: {
                backgroundColor: colors.surfaceElevated,
                border: `1px solid ${colors.border}`,
                borderRadius: customTheme.radius.md,
                fontSize: '0.6875rem',
                padding: '6px 10px',
              },
            },
          },
          MuiIconButton: {
            styleOverrides: {
              root: {
                padding: 6,
              },
              sizeSmall: {
                padding: 4,
              },
            },
          },
          MuiSvgIcon: {
            styleOverrides: {
              root: {
                fontSize: '1.25rem',
              },
              fontSizeSmall: {
                fontSize: '1rem',
              },
              fontSizeLarge: {
                fontSize: '1.5rem',
              },
            },
          },
          MuiMenu: {
            styleOverrides: {
              paper: {
                backgroundColor: colors.surfaceElevated,
                border: `1px solid ${colors.border}`,
                borderRadius: customTheme.radius.lg,
              },
            },
          },
        },
      }),
    [mode, colors, direction]
  );

  return (
    <ColorModeContext.Provider value={{ mode, setMode: setColorMode }}>
      <CacheProvider value={cache}>
        <ThemeProvider theme={theme}>{children}</ThemeProvider>
      </CacheProvider>
    </ColorModeContext.Provider>
  );
};
